import { useGetViewJobRelevantJobsQuery } from './__generated__/findViewJobRelevantJobs.gqlgenerated';
import { ViewJobDislikedJobCard } from './ViewJobDislikedJobCard';
import { useSetVanishingBottomToastPropsContext } from '../../../../../../ace/components/VanishingBottomToast/SetVanishingBottomToastPropsContext';
import { JobCard, type JobCardData } from '../../../../../../jobcard/components/JobCard/JobCard';
import { DislikedJobsProvider } from '../../../../../../jobcard/context/DislikedJobsContext';
import { Box, Heading, ScrollView } from '../../../../../../mads';
import { useI18n } from '../../../../../../shared/I18n';
import {
    JSTLandingScreen,
    JSTViewJobTarget,
} from '../../../../../../shared/native-modules/tare/JSTLandingScreen';
import navigateTo from '../../../../../../shared/native-modules/tare/navigateTo';
import { prepareJobClickUrl } from '../../../../../../shared/util/urlUtils';
import { useViewjobProctorAllocation } from '../../../../../proctor/__generated__/ViewjobProctorContext';
import { MobVjCompanyConquestingTst } from '../../../../../proctor/__generated__/ViewjobProctorSpecifications';
import { useViewjobOneGraphModel } from '../../../../context/ViewJobDataContext';
import { ViewJobDivider } from '../../Divider/ViewJobDivider';
import { JobCardLoadingIndicator } from '../../LoadingIndicator/LoadingIndicator';

import { colors } from '@indeed/ifl-tokens';
import {
    AvroOptional,
    log as logReceivedFlag,
} from '@indeed/signals-schema/com/indeed/signals/schema/profile/MobRcvFlag';
import React, { useEffect } from 'react';
import { StyleSheet } from 'react-native';

/** Maps proctor bucket values to the number of competitor jobs to fetch */
const BUCKET_TO_JOB_LIMIT: Record<number, number> = {
    3: 3,
    4: 15,
    5: 30,
};

export function CompanyConquestingJobCards({
    jobKey,
    loggedIn,
}: Readonly<{
    jobKey: string;
    loggedIn: boolean;
}>) {
    const setVanishingBottomToastProps = useSetVanishingBottomToastPropsContext();
    const i18n = useI18n();
    const viewjobOneGraphModel = useViewjobOneGraphModel();

    const companyName = viewjobOneGraphModel?.employer?.companyName;

    const { value: mobVjCompanyConquestingTstGrp } = useViewjobProctorAllocation(
        MobVjCompanyConquestingTst,
    );
    const jobLimit = BUCKET_TO_JOB_LIMIT[mobVjCompanyConquestingTstGrp];
    const mobVjCompanyConquestingTstActive = jobLimit !== undefined;
    const shouldShow =
        mobVjCompanyConquestingTstActive &&
        viewjobOneGraphModel?.job?.shouldClientSideLoadCompetitorsJobs;

    const { data, loading, error } = useGetViewJobRelevantJobsQuery({
        variables: {
            tag: 'jobKey',
            payload: jobKey,
            isLoggedIn: loggedIn,
            context: 'vjCompetitorsJobs',
            placementId: 'indeed-competitorjobs',
            limit: jobLimit,
        },
        skip: !shouldShow,
    });

    const jobCardsData = data?.findRelevantJobs?.results
        ? data?.findRelevantJobs?.results.map((result) => result.job)
        : [];

    useEffect(() => {
        if (loading || !shouldShow || !viewjobOneGraphModel?.mobvjtk) {
            return;
        }

        const isExpired = viewjobOneGraphModel.job?.isExpired;

        if (error) {
            logReceivedFlag({
                mobtk: viewjobOneGraphModel.mobvjtk,
                parentEventType: 'mobviewjob',
                flag: AvroOptional.string(
                    isExpired
                        ? 'expiredVjCompanyRecommendationsError'
                        : 'retargetedCompanyRecommendationsError',
                ),
                ts: AvroOptional.long(new Date().getTime()),
            });
        } else if (jobCardsData.length === 0) {
            logReceivedFlag({
                mobtk: viewjobOneGraphModel.mobvjtk,
                parentEventType: 'mobviewjob',
                flag: AvroOptional.string(
                    isExpired
                        ? 'expiredVjCompanyRecommendationsEmpty'
                        : 'retargetedCompanyRecommendationsEmpty',
                ),
                ts: AvroOptional.long(new Date().getTime()),
            });
        } else {
            logReceivedFlag({
                mobtk: viewjobOneGraphModel.mobvjtk,
                parentEventType: 'mobviewjob',
                flag: AvroOptional.string(
                    isExpired
                        ? 'expiredVjCompanyRecommendationsShown'
                        : 'retargetedCompanyRecommendationsShown',
                ),
                ts: AvroOptional.long(new Date().getTime()),
            });
        }
    }, [
        loading,
        shouldShow,
        error,
        jobCardsData.length,
        viewjobOneGraphModel?.mobvjtk,
        viewjobOneGraphModel?.job?.isExpired,
    ]);

    if (!shouldShow) {
        return null;
    }

    if (loading) {
        return <JobCardLoadingIndicator />;
    }

    return jobCardsData.length > 0 ? (
        <DislikedJobsProvider>
            <Heading
                level={4}
                id="jobDescriptionTitleHeading"
                sx={{
                    color: colors.neutral['1000'],
                    marginBottom: 4,
                    marginTop: 24,
                }}
            >
                {i18n.gettext('Explore other jobs')}
            </Heading>
            {companyName && (
                <Heading
                    level={2}
                    id="jobDescriptionTitleHeading"
                    sx={{
                        color: colors.neutral['800'],
                        fontWeight: 'normal',
                        marginBottom: 8,
                    }}
                >
                    {i18n.gettext('Discover opportunities beyond {0}.', [companyName])}
                </Heading>
            )}
            <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                sx={styles.container}
                scrollEventThrottle={16}
            >
                {jobCardsData.map((job, index) => {
                    const cardWidth = 320;

                    return (
                        <Box key={job.key} sx={styles.jobCardContainer}>
                            <JobCard
                                data={job}
                                viewJobFrom={'vjCompetitorsJobs'}
                                dislikeJobPageId={'coworker'}
                                containerStyle={{ width: cardWidth }}
                                webTargetUrl={prepareJobClickUrl(job.tracking?.jobClick?.url)}
                                onPressJobCard={(job: JobCardData) => {
                                    if (job.tracking) {
                                        const target = job.tracking?.jobClick?.url
                                            ? JSTViewJobTarget.url(job.tracking.jobClick.url)
                                            : JSTViewJobTarget.jobKey(job.key);
                                        navigateTo(
                                            JSTLandingScreen.viewJob(target),
                                            'rjp_mob.vjCompetitorsJobs',
                                        ).catch((err) =>
                                            console.error(
                                                'Failed to native navigate to View Job: ',
                                                err,
                                            ),
                                        );
                                    }
                                }}
                                onJobSaved={
                                    loggedIn
                                        ? () => {
                                              setVanishingBottomToastProps({
                                                  message: i18n.gettext('Job saved to My Jobs'),
                                                  variant: 'notification',
                                                  durationInMs: 3000,
                                              });
                                          }
                                        : undefined
                                }
                                DislikedJobCard={ViewJobDislikedJobCard}
                                loggedIn={loggedIn}
                                onJobCardImpression={
                                    index === 0
                                        ? () => {
                                              logReceivedFlag({
                                                  mobtk: viewjobOneGraphModel.mobvjtk,
                                                  parentEventType: 'mobviewjob',
                                                  flag: AvroOptional.string(
                                                      viewjobOneGraphModel.job?.isExpired
                                                          ? 'expiredVjCompanyRecommendationsSeen'
                                                          : 'retargetedCompanyRecommendationsSeen',
                                                  ),
                                                  ts: AvroOptional.long(new Date().getTime()),
                                              });
                                          }
                                        : undefined
                                }
                            />
                        </Box>
                    );
                })}
            </ScrollView>
            <ViewJobDivider />
        </DislikedJobsProvider>
    ) : null;
}

const styles = StyleSheet.create({
    jobCardContainer: {
        marginRight: 8,
        paddingRight: 16,
        display: 'flex',
        flexDirection: 'row',
    },
    container: {
        paddingTop: 15,
        paddingBottom: 25,
    },
    showMoreContainer: {
        paddingRight: 16,
    },
    showMoreButton: {
        width: 243,
        alignSelf: 'flex-start',
    },
});
